import { z } from 'zod';
import {
  CONFIGURATOR_SLOT_IDS,
  FURNITURE_TYPES,
  type ConfiguratorSlotId,
  type FurnitureType,
} from '../domain/configurator-vocabularies';

/**
 * Контракты конфигуратора — Block 6 Phase 1 (ADR-0015 §Q2 + §Q6).
 *
 * Используется UI и MSW в Phase 1 (echo handler), региональным API в Phase 9+
 * (`apps/edge/src/routes/configurator.ts` + Postgres `configurator_drafts`).
 *
 * Любое изменение контракта → новый ADR (P-011).
 *
 * ── State shape vs payload shape (ADR-0015 §Q2) ──────────────────────────
 * `ConfiguratorDraftSchema` описывает state в `configuratorStore` —
 * `type` может быть `null` (до выбора типа на Экране 2.6.1).
 *
 * `ConfiguratorDraftPayloadSchema` описывает body запроса
 * `POST /api/configurator/draft` — `type` обязателен (нельзя сохранить
 * draft без типа; синхронизация происходит во время сборки на 2.6.2).
 *
 * Симметрично будущей Postgres-таблице `configurator_drafts`:
 *   id UUID PK, user_id UUID FK, type FurnitureType NOT NULL,
 *   slots JSONB NOT NULL, created_at, updated_at.
 *
 * ── 5-slot invariant (ADR-0015 §Q2) ──────────────────────────────────────
 * Массив `slots` всегда содержит ровно 5 элементов с уникальными
 * `id ∈ {facade, countertop, corpus, handles, hooks}`. Без этого инварианта
 * возможны дубли (5x facade), что ломает `isComplete()` и UI 2.6.2.
 *
 * Refinement применяется на уровне массива (после `.length(5)`).
 *
 * ── `.strict()` ──────────────────────────────────────────────────────────
 * Защита от data leak / injection (см. `docs/examples/zod-contract.ts`).
 * Если Phase 9+ backend начнёт возвращать дополнительные поля (например,
 * `lastEditedAt`), фронт **отвергнет** ответ через `apiClient.configurator.*`
 * — нужный сигнал для контракт-amendment ADR.
 */

// ── Furniture type enum ──────────────────────────────────────────────────
// Type-coercion `as unknown as [T, ...T[]]` — паттерн из `paint.ts:30-40`
// (конвертация `Object.freeze([...] as const)` в формат, ожидаемый `z.enum`).
const furnitureTypeValues = FURNITURE_TYPES as unknown as [FurnitureType, ...FurnitureType[]];
export const FurnitureTypeSchema = z.enum(furnitureTypeValues);

// ── Slot id enum ─────────────────────────────────────────────────────────
const slotIdValues = CONFIGURATOR_SLOT_IDS as unknown as [
  ConfiguratorSlotId,
  ...ConfiguratorSlotId[],
];
export const ConfiguratorSlotIdSchema = z.enum(slotIdValues);

// ── ConfiguratorSlot (атом state'а) ──────────────────────────────────────
// `selectedItemId: string | null` — Global_ID товара (P-001 verbatim) или
// `null` для пустого слота. Slot всегда существует в массиве, поле всегда
// присутствует в JSON; `null` явно сигналит «не выбрано». Симметрия с
// Phase 9+ Postgres `slots JSONB` — null-safe.
//
// `.min(1)` — non-empty Global_ID. P-001 запрещает нормализацию, но пустая
// строка не может быть валидным Global_ID — всегда `null` для пустого слота.
export const ConfiguratorSlotSchema = z
  .object({
    id: ConfiguratorSlotIdSchema,
    selectedItemId: z.string().min(1).nullable(),
  })
  .strict();
export type ConfiguratorSlot = z.infer<typeof ConfiguratorSlotSchema>;

// ── 5-slot array invariant (refinement) ──────────────────────────────────
// `.length(5)` обязателен; refinement проверяет что все 5 канонических id
// присутствуют ровно по одному (нет дублей, нет пропусков).
const ConfiguratorSlotsArraySchema = z
  .array(ConfiguratorSlotSchema)
  .length(5)
  .refine(
    (slots) => {
      const ids = new Set(slots.map((s) => s.id));
      if (ids.size !== 5) return false;
      return CONFIGURATOR_SLOT_IDS.every((canonical) => ids.has(canonical));
    },
    {
      message:
        'slots must contain exactly 5 unique canonical slot ids: facade, countertop, corpus, handles, hooks',
    },
  );

// ── Draft state (live store shape, до отправки на бэк) ───────────────────
// `type: nullable` — пользователь может быть на Экране 2.6.1 (выбор типа
// ещё не сделан); store rehydrate'ит persisted state с `type=null` если
// localStorage пуст или draft был сброшен через `resetAll`.
export const ConfiguratorDraftSchema = z
  .object({
    type: FurnitureTypeSchema.nullable(),
    slots: ConfiguratorSlotsArraySchema,
  })
  .strict();
export type ConfiguratorDraft = z.infer<typeof ConfiguratorDraftSchema>;

// ── POST /api/configurator/draft request body ────────────────────────────
// Server payload — `type` обязателен. Без типа draft не имеет смысла
// (нельзя сохранить «пустой» draft без типа мебели).
export const ConfiguratorDraftPayloadSchema = z
  .object({
    type: FurnitureTypeSchema,
    slots: ConfiguratorSlotsArraySchema,
  })
  .strict();
export type ConfiguratorDraftPayload = z.infer<typeof ConfiguratorDraftPayloadSchema>;

// ── POST /api/configurator/draft response ────────────────────────────────
// Phase 1 MSW echo: `{success: true, draftId: crypto.randomUUID()}`.
// Phase 9+ backend: same shape, `draftId` from Postgres `gen_random_uuid()`.
// Phase 9+ может расширить response полями `createdAt` / `updatedAt` (data-model
// схема `configurator_drafts`) — потребует amendment ADR (P-011 enforced
// `.strict()` блокирует undocumented поля).
export const ConfiguratorDraftResponseSchema = z
  .object({
    success: z.literal(true),
    draftId: z.string().uuid(),
  })
  .strict();
export type ConfiguratorDraftResponse = z.infer<typeof ConfiguratorDraftResponseSchema>;

// ── GET /api/configurator/photo?type={type} query ────────────────────────
export const ConfiguratorPhotoQuerySchema = z
  .object({
    type: FurnitureTypeSchema,
  })
  .strict();
export type ConfiguratorPhotoQuery = z.infer<typeof ConfiguratorPhotoQuerySchema>;

// ── GET /api/configurator/photo response ─────────────────────────────────
// Phase 1 MSW: placeholder URL `/configurator-photos/{type}.png`.
// Phase 9+ backend: R2 signed URL с долгим Cache-Control.
export const ConfiguratorPhotoResponseSchema = z
  .object({
    url: z.string().min(1),
  })
  .strict();
export type ConfiguratorPhotoResponse = z.infer<typeof ConfiguratorPhotoResponseSchema>;
